<div data-testid="purchase-request-page" class="p-6 flex-table-page">
  <!-- Page Header -->
  <div class="mb-6 flex justify-between items-center flex-table-header">
    <h2 class="text-2xl font-semibold text-gray-800">
      {{ "purchase_request.title" | translate }}
    </h2>
  </div>

  <!-- Search Form -->
  <app-search-form
    data-testid="purchase-request-search-form"
    [searchFormat]="searchFormat()"
    (formSearch)="onSearch($event)"
    (formReset)="onReset()"
  >
    <div slot="actions" class="flex gap-2">
      <app-button
        [testId]="'purchase-request-column-config-btn'"
        type="success"
        [outlined]="true"
        icon="view_column"
        (emitOnClick)="onColumnConfigClick($event)"
      ></app-button>
      <app-status-filter
        [primaryButton]="primaryButton()"
        (primaryButtonClick)="onAddPurchaseRequest()"
      ></app-status-filter>
    </div>
  </app-search-form>

  <!-- Data Table -->
  <div class="flex-table-section">
    <advanced-data-table
      data-testid="purchase-request-table"
      #advancedTable
      [dataSource]="dataSource()"
      [defaultColumns]="defaultColumns()"
      [flexibleColumns]="flexibleColumns()"
      [loading]="loading()"
      [rows]="searchParams().size"
      [first]="(searchParams().page - 1) * searchParams().size"
      [totalRecords]="totalRecords()"
      [showInternalColumnConfigButton]="false"
      scrollHeight="flex"
      class="w-full"
      (pageChange)="onPageChange($event)"
      (sortChange)="onSortChange($event)"
    >
    <!-- Custom Template for Status -->
    <ng-template
      #customTemplate
      let-value
      let-rowData="rowData"
      let-column="column"
    >
      @if (column.prop === "status") {
        <!-- Status -->
        <div class="flex items-center justify-center gap-1">
          <p-tag
            data-testid="purchase-request-status-tag"
            [value]="value | statusLabel"
            [style]="value | statusStyle"
            styleClass="status-tag"
            [pTooltip]="value | statusLabel"
            tooltipPosition="top"
          ></p-tag>
        </div>
      }

      <!-- Custom Template for Creation Date -->
      @if (column.prop === "creationDate") {
        <span class="text-xs text-gray-600">
          {{ formatDate(value) }}
        </span>
      }

      <!-- Custom Template for Schedule Date -->
      @if (column.prop === "scheduleDate") {
        <span class="text-gray-600" style="font-size: 12px">
          {{ formatScheduleDate(value) }}
        </span>
      }

      <!-- Custom Template for Purchase Order -->
      @if (column.prop === "purchase_order") {
        @if (rowData.activeOrders && rowData.activeOrders.length > 0) {
          <div class="flex flex-wrap gap-1">
            @for (order of rowData.activeOrders; track order.id) {
              <a
                [href]="'/procurement/purchase-order/view/' + order.id"
                target="_blank"
                class="text-xs text-blue-600 hover:text-blue-800 underline"
                pTooltip="{{
                  'purchase_request.actions.view_purchase_order' | translate
                }}"
                tooltipPosition="top"
              >
                {{ order.series }}
              </a>
              @if (!$last) {
                <span class="text-xs text-gray-400">,</span>
              }
            }
          </div>
        } @else {
          <span class="text-xs text-gray-400">-</span>
        }
      }
    </ng-template>

    <!-- Custom Template for Actions -->
    <ng-template #actionsTemplate let-rowData>
      <div class="inline-flex items-center gap-2 whitespace-nowrap">
        <!-- View button -->
        @if (canRead()) {
         <button
            [attr.data-testid]="'purchase-request-action-view-' + rowData.id"
            type="button"
            (click)="onActionClick('view', rowData)"
            class="action-link action-view cursor-pointer"
          >
            {{ "common.actions.view" | translate }}
          </button>
        }

        <!-- Others button - only visible if there are more than 1 action and user has permissions -->
        @if (
          getPurchaseRequestActions(rowData).length > 1 &&
          hasEnabledPurchaseRequestActions(rowData)
        ) {
          <button
            [attr.data-testid]="'purchase-request-action-others-' + rowData.id"
            type="button"
            class="action-link action-others cursor-pointer"
            aria-haspopup="menu"
            (click)="toggleMenu($event, rowData)"
          >
            {{ "common.actions.others" | translate }}
          </button>
        }
      </div>
    </ng-template>
    </advanced-data-table>
  </div>
</div>

<!-- Single Menu for All Actions -->
<p-menu
  data-testid="purchase-request-actions-menu"
  #actionMenu
  [popup]="true"
  [model]="currentMenuModel()"
  appendTo="body"
  [baseZIndex]="2100"
  styleClass="actions-menu text-[0.75rem]"
  [autoZIndex]="true"
></p-menu>

<!-- Confirmation Dialog for Delete -->
<app-confirmation-dialog
  [testId]="'purchase-request-delete-dialog'"
  [visible]="showDeleteDialog()"
  [title]="'purchase_request.dialog.confirm_delete.title' | translate"
  [message]="'purchase_request.dialog.confirm_delete.message' | translate"
  [buttons]="deleteDialogButtons"
  (emitOnClick)="onDeleteConfirm($event)"
></app-confirmation-dialog>

<!-- Confirmation Dialog for Approve -->
<app-confirmation-dialog
  [testId]="'purchase-request-approve-dialog'"
  [visible]="showApproveDialog()"
  [title]="'purchase_request.dialog.confirm_approve.title' | translate"
  [message]="'purchase_request.dialog.confirm_approve.message' | translate"
  [buttons]="approveDialogButtons"
  (emitOnClick)="onApproveConfirm($event)"
></app-confirmation-dialog>

<!-- Confirmation Dialog for Reject -->
<app-confirmation-dialog
  [testId]="'purchase-request-reject-dialog'"
  [visible]="showRejectDialog()"
  [title]="'purchase_request.dialog.confirm_reject.title' | translate"
  [message]="'purchase_request.dialog.confirm_reject.message' | translate"
  [buttons]="rejectDialogButtons"
  (emitOnClick)="onRejectConfirm($event)"
></app-confirmation-dialog>

<!-- Status Change Dialog -->
<p-dialog
  data-testid="purchase-request-status-change-dialog"
  [(visible)]="showStatusChangeDialog"
  [modal]="true"
  [closable]="true"
  [draggable]="false"
  [resizable]="false"
  [style]="{ width: '600px', minHeight: '400px' }"
  [styleClass]="'status-change-dialog'"
>
  <ng-template pTemplate="header">
    <h3 class="text-lg font-semibold">
      {{ "purchase_request.dialog.change_status.title" | translate }}
    </h3>
  </ng-template>

  <div>
    <div class="mb-6">
      <h4 class="font-medium text-gray-900 mb-2" style="font-size: 14px">
        {{ "purchase_request.dialog.change_status.current_status" | translate }}
      </h4>
      <div class="flex items-center justify-start gap-1 mb-2">
        <p-tag
          data-testid="purchase-request-current-status-tag"
          [value]="selectedPurchaseRequest()?.status || '' | statusLabel"
          [style]="selectedPurchaseRequest()?.status || '' | statusStyle"
          styleClass="status-tag"
          [pTooltip]="selectedPurchaseRequest()?.status || '' | statusLabel"
          tooltipPosition="top"
          tooltipStyleClass="custom-tooltip"
          [showDelay]="300"
          [hideDelay]="0"
        />
      </div>
    </div>

    <div class="mb-4">
      <span
        class="block font-medium text-gray-700 mb-3"
        style="font-size: 14px"
        >{{
          "purchase_request.dialog.change_status.select_new_status" | translate
        }}</span
      >
      <p-select
        data-testid="purchase-request-new-status-select"
        [options]="getNextPossibleStatuses()"
        [(ngModel)]="selectedNewStatus"
        optionLabel="label"
        optionValue="value"
        placeholder="{{
          'purchase_request.dialog.change_status.select_option' | translate
        }}"
        class="w-full custom-select"
        [style]="{ minHeight: '36px' }"
        [appendTo]="'body'"
        [showClear]="true"
      ></p-select>
    </div>

    <div class="bg-blue-50 border border-blue-200 rounded-lg p-2">
      <div class="flex items-start">
        <i class="pi pi-info-circle text-blue-500 mr-2 mt-0.5"></i>
        <div>
          <p class="text-blue-800 font-medium mb-1" style="font-size: 12px">
            {{ "purchase_request.dialog.change_status.note_title" | translate }}
          </p>
          <p class="text-blue-700" style="font-size: 12px">
            {{
              "purchase_request.dialog.change_status.note_message" | translate
            }}
          </p>
        </div>
      </div>
    </div>
  </div>

  <ng-template pTemplate="footer">
    <div class="flex justify-end gap-2">
      <app-button
        [testId]="'purchase-request-status-change-cancel-btn'"
        [label]="'purchase_request.dialog.actions.cancel' | translate"
        (emitOnClick)="onStatusChangeConfirm('cancel')"
        [outlined]="true"
        type="secondary"
      ></app-button>
      <app-button
        [testId]="'purchase-request-status-change-confirm-btn'"
        [label]="'purchase_request.dialog.actions.change' | translate"
        (emitOnClick)="onStatusChangeConfirm('confirm')"
        type="info"
        [disabled]="!selectedNewStatus()"
      ></app-button>
    </div>
  </ng-template>
</p-dialog>

<!-- Toast Messages -->
<p-toast data-testid="purchase-request-toast"></p-toast>
